'use client';

import classNames from 'classnames';
import Link, { LinkProps } from 'next/link';
import React, { FunctionComponent, MouseEventHandler, PropsWithChildren } from 'react';

import { PropsWithClassname } from '@/app/types';
//TODO: fix jest alias imports
// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import { useIsIosOs } from '../../providers/ConfigProvider';

export enum ButtonStyle {
  PRIMARY = 'primary',
  PRIMARY_OUTLINED = 'primary-outlined',
  PRIMARY_LINK = 'primary-link',
  SUCCESS_OUTLINED = 'success-outlined',
  NEUTRAL_LINK = 'neutral-link',
  FACEBOOK = 'facebook',
  X = 'x',
  WHATSAPP = 'whatsapp',
  INSTAGRAM = 'instagram',
  //   SECONDARY = 'secondary',
  //   SECONDARY_OUTLINED = 'secondary-outlined',
}

export enum ButtonSize {
  //   SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  XL = 'xl',
}

export enum ButtonHTMLTag {
  BUTTON = 'button',
  LINK = 'link',
}

interface LinkSpecificProps {
  htmlTag: ButtonHTMLTag.LINK;
  href: LinkProps['href'];
  disabled?: boolean;
  onClick?: () => void;
  target?: string;
}

interface ButtonSpecificProps {
  htmlTag: ButtonHTMLTag.BUTTON;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
}

export interface ButtonProps extends PropsWithChildren<PropsWithClassname> {
  style?: ButtonStyle;
  size?: ButtonSize;
  tagSpecificProps: LinkSpecificProps | ButtonSpecificProps;
  testId?: string;
}

const Button: FunctionComponent<ButtonProps> = ({
  style = ButtonStyle.PRIMARY,
  size = ButtonSize.MEDIUM,
  tagSpecificProps,
  children,
  className,
  testId,
}) => {
  const isIosOs = useIsIosOs();

  const BaseHTMLTag = (
    props: LinkProps | React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  ) => {
    if (tagSpecificProps.htmlTag === ButtonHTMLTag.LINK) {
      return (
        <Link
          data-testid={testId}
          {...(props as LinkProps)}
          href={tagSpecificProps.disabled ? '#' : tagSpecificProps.href}
          target={tagSpecificProps.target}
          prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
          onClick={(ev) => {
            // we dont want to trigger onClick of parent element
            tagSpecificProps?.onClick?.();
            ev.stopPropagation();
          }}
        />
      );
    } else {
      return (
        <button
          data-testid={testId}
          onMouseDown={(e) => e.preventDefault()}
          {...(props as React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>)}
          // we dont want to trigger onClick of parent element
          onClick={(ev) => {
            ev.stopPropagation();
            tagSpecificProps?.onClick?.(ev);
          }}
          disabled={tagSpecificProps.disabled}
          type={tagSpecificProps.type || 'button'}
        />
      );
    }
  };

  const iosActiveStyles = {
    'active:bg-primary-dark': style === ButtonStyle.PRIMARY,
    'active:border-primary-mid': style === ButtonStyle.PRIMARY_OUTLINED && !tagSpecificProps.disabled,
    'active:bg-primary-light': style === ButtonStyle.PRIMARY_OUTLINED && !tagSpecificProps.disabled,
    'active:border-success-mid': style === ButtonStyle.SUCCESS_OUTLINED,
    'active:bg-success-light': style === ButtonStyle.SUCCESS_OUTLINED,
    'active:bg-social-facebook-dark': style === ButtonStyle.FACEBOOK,
    'active:bg-social-x-light': style === ButtonStyle.X,
    'active:bg-social-whatsapp-dark': style === ButtonStyle.WHATSAPP,
  };

  const otherPlatformsStyles = {
    'hover:bg-primary-dark': style === ButtonStyle.PRIMARY,
    'hover:border-primary-mid': style === ButtonStyle.PRIMARY_OUTLINED && !tagSpecificProps.disabled,
    'hover:bg-primary-light': style === ButtonStyle.PRIMARY_OUTLINED && !tagSpecificProps.disabled,
    'hover:border-success-mid': style === ButtonStyle.SUCCESS_OUTLINED,
    'hover:bg-success-light': style === ButtonStyle.SUCCESS_OUTLINED,
    'hover:bg-social-facebook-dark': style === ButtonStyle.FACEBOOK,
    'hover:bg-social-x-light': style === ButtonStyle.X,
    'hover:bg-social-whatsapp-dark': style === ButtonStyle.WHATSAPP,
  };

  const activeStyles = isIosOs ? iosActiveStyles : otherPlatformsStyles;

  return (
    <BaseHTMLTag
      className={classNames(
        className,
        'rounded-[4px] font-medium tracking-[2px] uppercase transition-colors cursor-pointer',
        {
          'px-[16px] py-[12px] text-[12px] leading-[16px]': size === ButtonSize.MEDIUM,
          'px-[24px] py-[20px] text-[12px]': size === ButtonSize.LARGE,
          'px-[24px] py-[24px] text-[14px]': size === ButtonSize.XL,
          'bg-primary-mid text-white': style === ButtonStyle.PRIMARY,
          'text-primary-mid border-2 border-primary-light bg-white': style === ButtonStyle.PRIMARY_OUTLINED,
          'text-success-mid border-2 border-success-midLight bg-white': style === ButtonStyle.SUCCESS_OUTLINED,
          'text-primary-mid bg-transparent': style === ButtonStyle.PRIMARY_LINK,
          'text-neutral-dark bg-transparent': style === ButtonStyle.NEUTRAL_LINK,
          'opacity-50 cursor-default': tagSpecificProps.disabled,
          'cursor-pointer': !tagSpecificProps.disabled,
          'bg-social-facebook-light text-white': style === ButtonStyle.FACEBOOK,
          'bg-social-x-dark text-white': style === ButtonStyle.X,
          'text-white bg-instagramGradient': style === ButtonStyle.INSTAGRAM,
          'bg-social-whatsapp-light text-white': style === ButtonStyle.WHATSAPP,
          ...activeStyles,
        },
      )}
    >
      {children}
    </BaseHTMLTag>
  );
};

export default Button;
